import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMedicalCheckupListSync } from '../../hooks/useMedicalCheckupListSync';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, TextField, IconButton, InputAdornment, MenuItem, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { DateUtils } from '../../helpers/dateUtils';
import { medicalCheckupsService } from '../../services/medicalCheckupsService';
import { AisErpStatus } from '../../models/enums/aisErpStatus';
import { MedicalCheckupResult, MedicalCheckupResultUtil } from '../../models/enums/medicalCheckupResult';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import { MedicalCheckupShortDto } from '../../models/medicalCheckups/medicalCheckupShortDto';
import { MedicalCheckupType, MedicalCheckupTypeUtil } from '../../models/enums/medicalCheckupType ';
import MedicalCheckupResultSelect from '../common/MedicalCheckupResultSelect';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import { DatePickerFilter } from '../common/DatePickerFilter';
import { LookupDto } from '../../models/common/lookup.dto';
import { MedicalCheckupStatus, MedicalCheckupStatusUtil } from '../../models/enums/medicalCheckupStatus';
import { RoleType } from '../../models/enums/roleType';
import { ShowForRoles } from '../common/permissions/ShowForRoles';
import TerminalsAutocomplete from '../common/TerminalsAutocomplete';
import RejectReasonsAutocomplete from '../common/RejectReasonsAutocomplete';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';
import { RejectReasonDto } from '../../models/medicalCheckups/rejectReasonDto';


export const MedicalCheckupsHistoryTable = () => {
    const [data, setData] = useState<MedicalCheckupShortDto[]>([]);
    const [page, setPage] = useState(0);
    const [dataCount, setDataCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());

    const [filter, setFilter] = useState(() => {
        const loaded = filterStorageService.loadFilter(FilterSection.MedicalCheckupsHistory, {
            fromDate: null as string | null,
            toDate: null as string | null,
            medicResult: null as MedicalCheckupResult | null,
            fio: '',
            company: null as CompanyShortDto | null,
            terminals: [] as LookupDto[],
            status: 'all' as 'all' | MedicalCheckupStatus,
            rejectReasons: [] as RejectReasonDto[],
        });
        if (loaded.medicResult !== MedicalCheckupResult.Rejected) {
            return { ...loaded, rejectReasons: [] as RejectReasonDto[] };
        }
        return { ...loaded, rejectReasons: loaded.rejectReasons ?? [] };
    });

    const isRejectReasonsEnabled = filter.medicResult === MedicalCheckupResult.Rejected;


    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.MedicalCheckupsHistory, filter);
    }, [filter]);

    const dataRef = useRef(data);
    const dataCountRef = useRef(dataCount);
    dataRef.current = data;
    dataCountRef.current = dataCount;

    const fetchData = useCallback(() => {
        const statuses = filter.status === 'all' ? [MedicalCheckupStatus.Verdict, MedicalCheckupStatus.Signing] : [filter.status];
        medicalCheckupsService.getByFilter({
            fromDate: DateUtils.toServerString(filter.fromDate),
            toDate: DateUtils.toServerString(filter.toDate),
            medicResult: filter.medicResult,
            statuses: statuses,
            fio: filter.fio,
            companyId: filter.company ? filter.company.id : null,
            terminalIds: filter.terminals.map(t => t.id),
            rejectReasonIds:
                filter.medicResult === MedicalCheckupResult.Rejected && filter.rejectReasons.length > 0
                    ? filter.rejectReasons.map(r => r.id)
                    : null,
            page: page,
            pageSize: rowsPerPage,
        })
            .then(r => {
                setData(r.data.data.items);
                setDataCount(r.data.data.count);
            })
            .catch(e => console.log(e));
    }, [page, rowsPerPage, filter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useMedicalCheckupListSync({
        listType: 'history',
        filter,
        page,
        rowsPerPage,
        onPatch: (items, count) => {
            setData(items);
            setDataCount(count);
        },
        onRefetch: fetchData,
        getCurrentState: () => ({ items: dataRef.current, count: dataCountRef.current }),
    });

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page when rowsPerPage changes
    };

    const filterChanged = (name: string, value: any) => {
        if (name === 'medicResult') {
            const nextRejectReasons =
                value === MedicalCheckupResult.Rejected ? filter.rejectReasons : [];
            setFilter({ ...filter, medicResult: value, rejectReasons: nextRejectReasons });
            setPage(0);
            return;
        }
        if (name === 'rejectReasons') {
            setFilter({ ...filter, rejectReasons: value });
            setPage(0);
            return;
        }
        setFilter({ ...filter, [name]: value });
    };

    const renderCheckupResult = (result: MedicalCheckupResult) => {
        const strValue = MedicalCheckupResultUtil.getStringValue(result);
        let color = result === MedicalCheckupResult.Rejected ? '#ec4a71' : '#5bba64';
        let backgroundColor = result === MedicalCheckupResult.Rejected ? '#fce4ea' : '#edfbee';

        return <Box display="inline-block" color={color} sx={{ backgroundColor: { backgroundColor }, borderRadius: "25px", paddingLeft: "15px", paddingRight: "15px" }}>{strValue}</Box>;
    };

    const renderCheckupStatus = (result: MedicalCheckupStatus) => {
        const strValue = MedicalCheckupStatusUtil.getStringValue(result);
        let color = result === MedicalCheckupStatus.SigningError ? '#ec4a71' : '#5bba64';
        let backgroundColor = result === MedicalCheckupStatus.SigningError ? '#fce4ea' : '#edfbee';

        return <Box display="inline-block" color={color} sx={{ backgroundColor: { backgroundColor }, borderRadius: "25px", paddingLeft: "15px", paddingRight: "15px" }}>{strValue}</Box>;
    };

    const updateRetryingState = useCallback((id: number, isRetrying: boolean) => {
        setRetryingIds(prev => {
            const next = new Set(prev);
            if (isRetrying) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const handleRetry = (e: React.MouseEvent, checkupId: number) => {
        e.stopPropagation();
        if (retryingIds.has(checkupId)) {
            return;
        }
        updateRetryingState(checkupId, true);
        setData(prev => prev.map(item => item.id === checkupId ? { ...item, aisErpStatus: null } : item));
        medicalCheckupsService.retryAis(checkupId)
            .then(response => {
                const success = response.data.data;
                setData(prev => prev.map(item => {
                    if (item.id !== checkupId) {
                        return item;
                    }
                    return {
                        ...item,
                        aisErpStatus: success ? AisErpStatus.Success : AisErpStatus.Error
                    };
                }));
            })
            .catch(() => {
                setData(prev => prev.map(item => item.id === checkupId ? { ...item, aisErpStatus: AisErpStatus.Error } : item));
            })
            .finally(() => updateRetryingState(checkupId, false));
    };

    const renderAisStatus = (item: MedicalCheckupShortDto) => {
        if (item.type === MedicalCheckupType.AlcoholTest) {
            return null;
        }

        if (!item.hasAisTerminal) {
            return null;
        }

        if (!item.aisErpStatus) {
            return null;
        }

        if (item.aisErpStatus === AisErpStatus.Success) {
            return (
                <Tooltip title="Отправлено в НИИАТ">
                    <span onClick={(e) => e.stopPropagation()}>
                        <CheckCircleIcon sx={{ color: '#5bba64' }} />
                    </span>
                </Tooltip>
            );
        }

        if (item.aisErpStatus === AisErpStatus.Error) {
            return (
                <Tooltip title="Ошибка отправки. Нажмите для повторной попытки">
                    <span onClick={(e) => e.stopPropagation()}>
                        <IconButton
                            size="small"
                            disabled={retryingIds.has(item.id)}
                            onClick={(e) => handleRetry(e, item.id)}
                        >
                            <HighlightOffIcon sx={{ color: '#ec4a71' }} />
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }

        return (
            <Tooltip title="Ожидает отправки в НИИАТ">
                <span onClick={(e) => e.stopPropagation()}>
                    <AccessTimeIcon sx={{ color: '#f0a202' }} />
                </span>
            </Tooltip>
        );
    };

    const handleCompanyChange = (newCompany: CompanyShortDto | null) => {
        setFilter((prev) => ({
            ...prev,
            company: newCompany,
        }));
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" marginBottom={1} mt={2.5}>
                <DatePickerFilter
                    label="Начало периода"
                    name="fromDate"
                    value={filter.fromDate}
                    onChange={(value, name) => filterChanged(name, value)}
                    style={{ width: 200, marginRight: 10, marginTop: 4 }}
                />

                <DatePickerFilter
                    label="Конец периода"
                    name="toDate"
                    value={filter.toDate}
                    onChange={(value, name) => filterChanged(name, value)}
                    style={{ width: 200, marginRight: 10, marginTop: 4 }}
                />

                <TextField
                    label="ФИО сотрудника"
                    name="fio"
                    value={filter.fio}
                    onChange={(e: { target: { name: string; value: any; }; }) => filterChanged(e.target.name, e.target.value)}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={{ width: 200, marginRight: 10 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setPage(0)}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: {
                            borderRadius: "10px",
                        }
                    }}
                />

                <MedicalCheckupResultSelect
                    name={'medicResult'}
                    value={filter.medicResult !== null ? filter.medicResult : undefined}
                    onChange={(e) => filterChanged(e.target.name, e.target.value || null)}
                />

                <RejectReasonsAutocomplete
                    label="Причина"
                    value={filter.rejectReasons}
                    disabled={!isRejectReasonsEnabled}
                    onChange={(reasons) => filterChanged('rejectReasons', reasons)}
                    style={{ width: 240, marginRight: 10 }}
                />

            </Box>
            <Box display="flex" alignItems="center" marginBottom={1}>

                <CompanyAutocomplete
                    company={filter.company}
                    onCompanyChange={handleCompanyChange}
                    style={{ width: 200, marginRight: 10 }}
                />

                <TerminalsAutocomplete
                    terminals={filter.terminals}
                    onTerminalsChange={(newTerminals) => filterChanged('terminals', newTerminals)}
                    style={{ width: 200, marginRight: 10 }}
                    multiple={true}
                />

                <TextField
                    select
                    label="Статус"
                    name="status"
                    value={filter.status}
                    onChange={(e) => filterChanged(e.target.name, e.target.value)}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={{ width: 200, marginRight: 10 }}
                    SelectProps={{ style: { borderRadius: "10px" } }}
                >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value={MedicalCheckupStatus.Verdict}>{MedicalCheckupStatusUtil.getStringValue(MedicalCheckupStatus.Verdict)}</MenuItem>
                    <MenuItem value={MedicalCheckupStatus.Signing}>{MedicalCheckupStatusUtil.getStringValue(MedicalCheckupStatus.Signing)}</MenuItem>
                </TextField>

            </Box>
            <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>№</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>ФИО сотрудника</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Клиент</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Тип осмотра</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Дата</Typography></TableCell>
                            <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Результат авто</Typography></TableCell>
                            </ShowForRoles>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Результат медика</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Статус</Typography></TableCell>
                            <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>НИИАТ</Typography></TableCell>
                            </ShowForRoles>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item) => (
                            <TableRow
                                key={item.id}
                                onClick={() => window.open(`/medical-checkups/${item.id}`)}
                                sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{item.id}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.fio}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.company.name}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{MedicalCheckupTypeUtil.getStringValue(item.type)}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {DateUtils.formatDateTimeWithTimeZone(item.date, item.timeShift)}
                                </TableCell>
                                <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                    <TableCell sx={{ fontWeight: "bold" }}>{renderCheckupResult(item.autoResult)}</TableCell>
                                </ShowForRoles>
                                <TableCell sx={{ fontWeight: "bold" }}>{renderCheckupResult(item.medicResult)}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{renderCheckupStatus(item.status)}</TableCell>
                                <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        {renderAisStatus(item)}
                                    </TableCell>
                                </ShowForRoles>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Всего: {dataCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={dataCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event: any, page: number) => setPage(page)}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={"Строк на странице:"}
                    labelDisplayedRows={({ from, to, count }: any): string => `Показано ${from}-${to} из ${count}`}
                />
            </Box>
        </Box>
    );
};